// src/plugins/analyticsPlugin.js
import analyticsService from '@/services/analyticsService';
import { isPublicPath } from '@/utils/publicRoutes';

/**
 * Vue Router plugin for automatic page view tracking
 */
export function setupAnalyticsRouterTracking(router) {
    if (import.meta.env.VITE_ENABLE_TRACKING !== 'true') {
        return;
    }

    router.afterEach((to, from) => {
        // Public promo and checkout pages report themselves through cookieless
        // gtag (Consent Mode, all storage denied). Firebase Analytics cannot run
        // there: it persists an app-instance id to the device, which is exactly
        // what a page shown without a cookie banner must not do.
        if (isPublicPath(to.path)) {
            return;
        }

        // Wait a bit for the page to load and user data to be available
        setTimeout(() => {
            const pageTitle = document.title || to.meta?.title || to.name || 'Unknown Page';
            const safePath = analyticsService.buildSafePath(to);

            analyticsService.trackPageView(pageTitle, safePath);
        }, 500);
    });
}

/**
 * Vue mixin for component-level analytics tracking
 */
export const analyticsMixin = {
    methods: {
        /**
         * Track a custom event from any component
         */
        $trackEvent(eventName, parameters = {}) {
            analyticsService.trackEvent(eventName, parameters);
        },

        /**
         * Track user actions from any component
         */
        $trackUserAction(action, category, details = {}) {
            analyticsService.trackUserAction(action, category, details);
        },

        /**
         * Track form submissions from any component
         */
        $trackFormSubmission(formName, formData = {}) {
            analyticsService.trackFormSubmission(formName, formData);
        },

        /**
         * Track errors from any component
         */
        $trackError(errorType, errorMessage, location) {
            analyticsService.trackError(errorType, errorMessage, location);
        },

        /**
         * Track search events from any component
         */
        $trackSearch(searchTerm, searchCategory, resultsCount) {
            analyticsService.trackSearch(searchTerm, searchCategory, resultsCount);
        },

        /**
         * Track file downloads from any component
         */
        $trackDownload(fileName, fileType) {
            analyticsService.trackDownload(fileName, fileType);
        }
    }
};

/**
 * Global error handler for analytics
 */
export function setupGlobalErrorTracking(app) {
    if (import.meta.env.VITE_ENABLE_TRACKING !== 'true') {
        return;
    }

    app.config.errorHandler = (error, instance, info) => {
        console.error('Vue Error:', error);
        
        analyticsService.trackError(
            'vue_error',
            error.message || 'Unknown Vue error',
            info || 'Unknown location'
        );
        
        // Re-throw the error so it's still logged to console
        throw error;
    };

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        analyticsService.trackError(
            'unhandled_promise_rejection',
            event.reason?.message || 'Unknown promise rejection',
            window.location.pathname
        );
    });

    // Track JavaScript errors
    window.addEventListener('error', (event) => {
        analyticsService.trackError(
            'javascript_error',
            event.message || 'Unknown JavaScript error',
            `${event.filename}:${event.lineno}:${event.colno}`
        );
    });
}